/**
 * Investment entity types matching backend schemas
 * Source: backend/app/schemas/investments.py, backend/app/domain/investments/enums.py
 */

/**
 * Investment state enum - represents current state of investment
 * Source: backend/app/domain/investments/enums.py
 */
export enum InvestmentState {
	PROSPECT = "prospect",
	RESERVED = "reserved",
	FINANCING = "financing",
	FUNDED = "funded",
	DECLARED = "declared",
	CANCELLED = "cancelled",
	EXPIRED = "expired",
	REJECTED = "rejected",
}

/**
 * Investment event types - actions/events that occur during lifecycle
 * Source: backend/app/domain/investments/enums.py
 */
export enum InvestmentEventType {
	CREATED = "created",
	PROPOSAL_SENT = "proposal_sent",
	RESERVATION_CONTRACT_SENT = "reservation_contract_sent",
	FINANCING_CONTRACT_SENT = "financing_contract_sent",
	COMMUNICATION_CONTRACT_SENT = "communication_contract_sent",
	CONTRACT_SIGNED = "contract_signed",
	CONTRACT_VOIDED = "contract_voided",
	CONTRACT_DECLINED = "contract_declined",
	CONTRACT_DELIVERY_FAILED = "contract_delivery_failed",
	INVOICE_SENT = "invoice_sent",
	RESERVATION_CREATED = "reservation_created",
	RESERVATION_EXTENDED = "reservation_extended",
	RESERVATION_RELEASED = "reservation_released",
	RESERVATION_EXPIRED = "reservation_expired",
	QUOTA_RESERVED = "quota_reserved",
	QUOTA_RELEASED = "quota_released",
	QUOTA_EXPIRED = "quota_expired",
	PAYMENT_RECEIVED = "payment_received",
	PAYMENT_CONFIRMED = "payment_confirmed",
	PAYMENT_REVERSED = "payment_reversed",
	TAX_SUBMITTED = "tax_submitted",
	TAX_ACCEPTED = "tax_accepted",
	TAX_REJECTED = "tax_rejected",
	DOCUMENT_UPLOADED = "document_uploaded",
	DOCUMENT_APPROVED = "document_approved",
	DOCUMENT_REJECTED = "document_rejected",
	STATE_CHANGED = "state_changed",
	AMOUNT_UPDATED = "amount_updated",
	NOTES_UPDATED = "notes_updated",
	PROMOTER_CHANGED = "promoter_changed",
	RETURN_PERCENTAGE_UPDATED = "return_percentage_updated",
	CANCELLED = "cancelled",
	REJECTED = "rejected",
}

/**
 * Investment document types - specific to investment lifecycle
 * Source: backend/app/domain/investments/enums.py
 */
export enum InvestmentDocumentType {
	INVESTMENT_PROPOSAL = "investment_proposal",
	RESERVATION_CONTRACT = "reservation_contract",
	FINANCING_CONTRACT = "financing_contract",
	COMMUNICATION_CONTRACT = "communication_contract",
	PAYMENT_RECEIPT = "payment_receipt",
	TAX_SUBMISSION_CERTIFICATE = "tax_submission_certificate",
}

/**
 * Re-export the canonical DocumentStatus from documentEnums to avoid divergence.
 */
export { DocumentStatus } from "../documentEnums";

/**
 * Base fields shared by all investment types (Hub and regular)
 * Used to avoid duplication between HubInvestment and InvestmentResponse
 */
export interface InvestmentBaseFields {
	uuid: string;
	amount: string; // Decimal with 2 decimal places
	return_percentage: string; // Decimal with 2 decimal places
	currency: string;
}

/**
 * Pagination parameters shared across investment queries
 */
export interface InvestmentPaginationParams {
	limit?: number;
	offset?: number;
}

/**
 * Summary model for investor data in investment responses
 * Source: backend/app/schemas/investors.py - InvestorSummary
 */
interface InvestorSummary {
	uuid: string;
	type: "individual" | "company";
	status: "active" | "inactive" | "pending";
	display_name: string;
	email?: string;
	tax_identifier?: string;
}

/**
 * Summary model for promoter data in investment responses
 * Source: backend/app/schemas/promoters.py - PromoterSummary
 */
interface PromoterSummary {
	uuid: string;
	name: string;
	tax_id: string;
	email?: string;
	phone?: string;
	incentive_type?: "film_audiovisual" | "performing_arts_music" | null;
}

/**
 * Summary model for promoter fiscal quota data
 * Source: backend/app/schemas/promoters.py - PromoterFiscalQuotaSummary
 */
interface PromoterFiscalQuotaSummary {
	uuid: string;
	fiscal_year: number;
	quota_amount: string;
	reserved_amount: string;
	completed_amount: string;
	utilization_percentage: number;
}

/**
 * Summary model for user data in investment events
 * Source: backend/app/schemas/users.py - UserSummary
 */
interface UserSummary {
	uuid: string;
	email: string;
	first_name?: string;
	last_name?: string;
	full_name: string;
	profile_picture_signed_url?: string;
}

/**
 * Request model for creating a new investment
 * Source: backend/app/schemas/investments.py - InvestmentCreateRequest
 */
export interface InvestmentCreateRequest {
	investor_uuid: string; // UUID of the investor
	promoter_quota_uuid: string; // UUID of the promoter fiscal quota
	amount: string; // Decimal string, must be > 0
	return_percentage: string; // Decimal string, must be > 0
	state?: InvestmentState; // Initial state, defaults to PROSPECT
	notes?: string; // Initial notes (max 1000 chars)
}

/**
 * Request model for updating investment via PATCH
 * Source: backend/app/schemas/investments.py - InvestmentUpdateRequest
 */
export interface InvestmentUpdateRequest {
	amount?: string; // New investment amount (Decimal string, > 0)
	return_percentage?: string; // New return percentage (Decimal string, > 0)
	promoter_quota_uuid?: string; // New promoter quota UUID (only allowed in PROSPECT state)
	state?: InvestmentState; // New state
	notes?: string; // Notes to append (max 1000 chars)
}

/**
 * Request for extending reservation
 * Source: backend/app/schemas/investment_actions.py - ExtendReservationRequest
 */
export interface ExtendReservationRequest {
	new_expires_at: string; // ISO datetime string - must be in the future
}

/**
 * Response model for investment summary data
 * Source: backend/app/schemas/investments.py - InvestmentResponse
 */
export interface InvestmentResponse extends InvestmentBaseFields {
	state: InvestmentState;
	archived: boolean;
	reservation_expires_at?: string | null; // ISO datetime string
	notes?: string | null;
	created_at?: string | null; // ISO datetime string
	updated_at?: string | null; // ISO datetime string

	// Rich summary objects instead of UUIDs
	investor: InvestorSummary;
	promoter: PromoterSummary;
	quota: PromoterFiscalQuotaSummary;
}

/**
 * Response model for investment event data
 * Source: backend/app/schemas/investments.py - InvestmentEventResponse
 */
export interface InvestmentEventResponse {
	uuid: string;
	event_type: InvestmentEventType;
	occurred_at: string; // ISO datetime string
	user?: UserSummary | null;
	metadata?: Record<string, unknown> | null;
	created_at: string; // ISO datetime string
}

/**
 * Response with document details
 * Source: backend/app/schemas/investment_documents.py - DocumentDetailsResponse
 */
export interface DocumentDetailsResponse {
	uuid: string;
	investment_uuid: string;
	doc_type: InvestmentDocumentType;
	status: DocumentStatus;
	name: string;
	title?: string | null;
	size_bytes: number;
	content_type: string;
	uploaded_at: string; // ISO datetime string
	uploaded_by: number;
	reviewed_at?: string | null; // ISO datetime string
	reviewed_by_user_id?: number | null;
	voided_at?: string | null; // ISO datetime string
	docusign_envelope_id?: string | null;
	docusign_status?: string | null;
}

/**
 * Response model for investment with all nested details
 * Source: backend/app/schemas/investments.py - InvestmentWithDetailsResponse
 */
export interface InvestmentWithDetailsResponse {
	investment: InvestmentResponse;
	events: InvestmentEventResponse[];
	documents: DocumentDetailsResponse[];
}

/**
 * Response for extending reservation
 * Source: backend/app/schemas/investment_actions.py - ExtendReservationResponse
 */
export interface ExtendReservationResponse {
	extension_granted: boolean;
	new_expires_at: string; // ISO datetime string
	notification_sent: boolean;
	extended_at: string; // ISO datetime string
}

/**
 * Query parameters for listing investments
 * Source: backend/app/api/routes/investments.py - list_investments
 */
export interface InvestmentQueryParams {
	// Filters
	state?: string; // Filter by state (PROSPECT, RESERVED, etc.)
	investor_uuid?: string; // Filter by investor UUID
	promoter_uuid?: string; // Filter by promoter UUID
	search?: string; // Search in investor name, promoter name, notes
	amount_min?: string; // Minimum investment amount
	amount_max?: string; // Maximum investment amount
	is_archived?: boolean; // Filter by archived status
	investor_type?: "individual" | "company"; // Filter by investor type
	// Sorting
	sort_by?: string; // Sort field: amount, return_percentage, created_at, state
	sort_direction?: "asc" | "desc"; // Sort direction
	// Pagination
	limit?: number; // Number of results to return (1-500, default 100)
	offset?: number; // Number of results to skip (>= 0, default 0)
}

/**
 * Investment filters for advanced searching
 */
export interface InvestmentFilters {
	states?: InvestmentState[];
	investor_uuids?: string[];
	promoter_uuids?: string[];
	amount_min?: string; // Decimal string
	amount_max?: string; // Decimal string
	return_percentage_min?: string; // Decimal string
	return_percentage_max?: string; // Decimal string
	created_after?: string; // ISO date string
	created_before?: string; // ISO date string
	reservation_expiring_before?: string; // ISO date string
	has_documents?: boolean;
	has_events?: boolean;
}

/**
 * Paginated investment list response
 * Source: backend/app/schemas/investments.py - InvestmentListResponse
 */
export interface InvestmentPagination {
	investments: InvestmentWithDetailsResponse[];
	total_count: number;
	limit: number;
	offset: number;
}

export interface Investment {
	id: string;
	ownerId: string; // Email of the user who owns this investment data
	projectName: string;
	investorName: string;
	investorId?: string;
	amount: number;
	percentage: number;
	status:
		| "active"
		| "pending"
		| "completed"
		| "contacted"
		| "proposal_sent"
		| "declined";
	startDate: Date;
	expectedReturn: number;
	type: "equity" | "debt" | "hybrid";
	lastModified: Date;
}
